import type { Policy } from '@kirin/api-client/contracts/policies';
import { PolicyKey, PolicyMode } from '@kirin/api-client/contracts/policies';

/** Shown below the YOLO Modes description when that policy row is visible (Monitor or Enforce). */
export const YOLO_MODES_DEPRECATION_MESSAGE =
  'This policy is deprecated. YOLO/auto mode detection is no longer needed as these modes are now standard practice. Set the policy to Off to remove it from this page.';

export const YOLO_MODES_OFF_CONFIRM_TITLE = 'Turn off YOLO Modes?';

export const YOLO_MODES_OFF_CONFIRM_DESCRIPTION =
  'Turning off YOLO Modes detection will permanently turn off and remove this deprecated policy. Continue?';

/**
 * YOLO Modes is hidden on the Policies page unless Monitor or Enforce is active.
 * Off or missing mode hides the row (new customers and anyone who turns it Off).
 */
export function shouldShowPolicyOnPoliciesPage(policy: Policy): boolean {
  if (policy.key !== PolicyKey.YoloModes) {
    return true;
  }
  return (
    policy.mode === PolicyMode.Monitor || policy.mode === PolicyMode.Enforce
  );
}

export function filterPoliciesForPoliciesPage(policies: Policy[]): Policy[] {
  return policies.filter(shouldShowPolicyOnPoliciesPage);
}

/**
 * After YOLO is turned Off, the API list no longer includes it for display; keep a snapshot
 * on screen briefly so a fade-out animation can run.
 */
export function mergeYoloExitSnapshotForDisplay(
  policies: Policy[],
  yoloExitSnapshot: Policy | null,
  suppressStaleYolo = false
): Policy[] {
  const policiesWithoutStaleYolo = suppressStaleYolo
    ? policies.filter(policy => policy.key !== PolicyKey.YoloModes)
    : policies;
  const filtered = filterPoliciesForPoliciesPage(policiesWithoutStaleYolo);
  if (
    yoloExitSnapshot?.key === PolicyKey.YoloModes &&
    !filtered.some(p => p.key === PolicyKey.YoloModes)
  ) {
    return [...filtered, yoloExitSnapshot];
  }
  return filtered;
}
